import type { LinkedinMessagingResponse, TransformedLinkedinMessage } from './types';

import { useQuery } from '@tanstack/react-query';
import { browserExtensionClient } from 'clients/BrowserExtensionClient/BrowserExtensionClient';
import { notificationsClient } from 'clients/NotificationsClient/NotificationsClient';
import { queryClient } from 'config/queryClient';
import { encodeBase64 } from 'helpers/base64';
import { useEffect, useRef, useState } from 'react';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { fallbackSaveReceivedMessages } from './fallbackSaveReceivedMessages';
import { useLinkedinConfig } from './linkedin/useLinkedinConfig';
import { MessageReceivedPlatform } from './platform.types';

const REFETCH_INTERVAL = 600 * 1000; // 10 minutes

export function useUnreadLinkedInMessages() {
  const { data: currentUser } = useCurrentUserQuery();

  const invalidationCountRef = useRef(0);

  const [shouldRunCountUnreadQuery, setShouldRunCountUnreadQuery] = useState(true);
  const { linkedinId, isLoggedIn } = useLinkedinConfig();

  useQuery(
    ['countUnreadLinkedInMessages', linkedinId],
    async () => {
      const response = await browserExtensionClient.sendMessage('proxyHttpRequest', {
        url: `https://www.linkedin.com/voyager/api/voyagerMessagingGraphQL/graphql?queryId=messengerConversations.0fd27f53c2a17c81a3c2370c94ab3068&variables=(categories:List(INBOX),mailboxUrn:urn%3Ali%3Afsd_profile%3A${linkedinId},read:false)`,
        method: 'GET',
        addHeadersFrom: 'linkedInClassic',
        headers: {
          accept: 'application/json',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
        },
      });

      return response;
    },
    {
      enabled: !!currentUser && !!linkedinId && isLoggedIn && shouldRunCountUnreadQuery,
      refetchInterval: REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      onSuccess: async (data) => {
        if (currentUser) {
          // FIXME: Error: "undefined" is not valid JSON https://onenr.io/0ZQW8kKoewW
          const jsonData: LinkedinMessagingResponse = JSON.parse(data.body);

          if (jsonData.errors?.some((error) => error.extensions.status === 403)) {
            if (invalidationCountRef.current < 3) {
              invalidationCountRef.current += 1;
              queryClient.invalidateQueries(['linkedInFsdProfileId']);
            } else {
              setShouldRunCountUnreadQuery(false);
            }
            throw new Error('403 Forbidden');
          }

          const unreads = jsonData.data.messengerConversationsBySyncToken.elements.filter(
            (element) => !element.read && !element.categories.includes('ARCHIVE'),
          );

          const onlyUnreadsFromCandidates = unreads.filter((unread) =>
            unread.conversationParticipants.every((participant) => participant.hostIdentityUrn.includes('fsd_profile')),
          );

          const messages = onlyUnreadsFromCandidates.map((message) => {
            const currentUserId = `urn:li:fsd_profile:${linkedinId}`;

            const [candidate] = message.conversationParticipants.filter(
              (participant) => participant.hostIdentityUrn !== currentUserId,
            );

            const candidateId = candidate.hostIdentityUrn.split(':fsd_profile:')[1];
            const esPersonId = `cvt-${candidateId}`;

            const lastMessage = message.messages.elements.sort((a, b) => b.deliveredAt - a.deliveredAt)[0];

            return {
              es_person_id: esPersonId,
              message_id: String(message.lastActivityAt),
              thread_id: encodeBase64(`${currentUser?.id}-${esPersonId}`),
              message_content: lastMessage.body.text,
            };
          });

          const candidatesToProcess = await notificationsClient.saveLinkedinMessagesNotifications({ messages });

          if (candidatesToProcess.data.candidates.length > 0) {
            const transformedMessagesMap = new Map<string, TransformedLinkedinMessage[]>();
            const currentUserId = `urn:li:fsd_profile:${linkedinId}`;

            onlyUnreadsFromCandidates.forEach((conversation) => {
              const [candidate] = conversation.conversationParticipants.filter(
                (participant) => participant.hostIdentityUrn !== currentUserId,
              );

              const candidateId = candidate.hostIdentityUrn.split(':fsd_profile:')[1];
              const esPersonId = `cvt-${candidateId}`;

              const transformedMessages: TransformedLinkedinMessage[] = conversation.messages.elements.map(
                (message) => {
                  const senderId = message.sender.hostIdentityUrn.split(':fsd_profile:')[1];
                  const fromSelf = message.sender.hostIdentityUrn === currentUserId;

                  return {
                    senderId,
                    content: message.body.text,
                    fromSelf,
                    sentAt: message.deliveredAt,
                  };
                },
              );

              transformedMessagesMap.set(esPersonId, transformedMessages);
            });

            for (const candidate of candidatesToProcess.data.candidates) {
              const transformedMessages = transformedMessagesMap.get(candidate.id) || [];

              if (transformedMessages.length === 0) continue;

              fallbackSaveReceivedMessages({
                currentUser,
                messages: transformedMessages,
                candidate,
                platform: MessageReceivedPlatform.LINKEDIN_CLASSIC,
              });
            }
          }
        }
      },
    },
  );

  useEffect(() => {
    if (currentUser) {
      invalidationCountRef.current = 0;
      setShouldRunCountUnreadQuery(true);
    }
  }, [currentUser, linkedinId]);
}
